import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiClient } from '../../lib/apiClient';
import { Trash2, Plus, Users } from '../icons/Icons';
import s from './shared.module.css';

interface Department {
  id: string;
  org_id: string;
  name: string;
  description: string;
  manager_id: string | null;
  is_active: boolean;
  created_at: string;
  member_count?: number;
  manager_name?: string;
}

interface DepartmentMember {
  id: string;
  user_id: string;
  display_name: string;
  email: string;
  role: string;
}

interface OrgMember {
  id: string;
  user_id: string;
  display_name: string;
  email?: string;
  user_type: string;
}

export default function DepartmentManagement() {
  const { organization, user, orgMember } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDept, setSelectedDept] = useState<Department | null>(null);
  const [deptMembers, setDeptMembers] = useState<DepartmentMember[]>([]);

  const [deptName, setDeptName] = useState('');
  const [deptDescription, setDeptDescription] = useState('');
  const [deptManagerId, setDeptManagerId] = useState<string>('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadOrgMembers = useCallback(async () => {
    if (!organization) return;
    try {
      const data = await apiClient.get<OrgMember[]>(
        `/orgs/${organization.id}/members?isActive=true&types=owner,admin,manager`
      );
      setOrgMembers(data || []);
    } catch (err) {
      console.error('Error loading org members:', err);
    }
  }, [organization]);

  const loadDepartments = useCallback(async () => {
    if (!organization) return;
    setLoading(true);
    try {
      const data = await apiClient.get<Department[]>(
        `/orgs/${organization.id}/departments?withCounts=true`
      );
      setDepartments(data || []);
    } catch (err) {
      console.error('Error loading departments:', err);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, [organization]);

  const loadDepartmentMembers = useCallback(async (deptId: string) => {
    try {
      const members = await apiClient.get<DepartmentMember[]>(`/departments/${deptId}/members`);
      setDeptMembers(members || []);
    } catch (err) {
      console.error('Error loading department members:', err);
      setDeptMembers([]);
    }
  }, []);

  useEffect(() => {
    loadOrgMembers();
    loadDepartments();
  }, [loadOrgMembers, loadDepartments]);

  useEffect(() => {
    if (selectedDept) {
      loadDepartmentMembers(selectedDept.id);
    }
  }, [selectedDept, loadDepartmentMembers]);

  const handleCreateDepartment = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!organization) return;

  setSaveError('');
  setSaving(true);

  try {
    await apiClient.post(`/orgs/${organization.id}/departments`, {
      name: deptName.trim(),
      description: deptDescription.trim(),
      manager_id: deptManagerId || null,
      is_active: true,
    });

    setShowCreateModal(false);
    setDeptName('');
    setDeptDescription('');
    setDeptManagerId('');
    setSaveError('');

    await loadDepartments();
  } catch (err: any) {
    console.error('Failed to create department:', err);
    setSaveError(err.message || 'Failed to create department. Please try again.');
  } finally {
    setSaving(false);
  }
};


  const handleUpdateDepartmentOwner = async (deptId: string, newManagerId: string | null) => {
    try {
      await apiClient.patch(`/departments/${deptId}`, { manager_id: newManagerId || null });
      loadDepartments();
      if (selectedDept?.id === deptId) {
        setSelectedDept(prev => prev ? { ...prev, manager_id: newManagerId } : null);
      }
    } catch (err: any) {
      alert(err.message || 'Failed to update department owner');
    }
  };

  const handleDeleteDepartment = async (deptId: string) => {
    if (!confirm('Delete this department? Members will not be affected.')) return;

    try {
      await apiClient.delete(`/departments/${deptId}`);
      loadDepartments();
      if (selectedDept?.id === deptId) {
        setSelectedDept(null);
      }
    } catch (err: any) {
      alert(err.message || 'Failed to delete department');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm('Remove this member from the department?')) return;

    try {
      await apiClient.delete(`/department-members/${memberId}`);
      if (selectedDept) {
        loadDepartmentMembers(selectedDept.id);
      }
      loadDepartments();
    } catch (err: any) {
      alert(err.message || 'Failed to remove member');
    }
  };

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#605e5c' }}>Loading departments...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, color: '#323130' }}>Departments</h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#605e5c' }}>
            Organize your team into departments for better access control
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className={s.createBtn}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={16} /> New Department
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedDept ? '1fr 1fr' : '1fr', gap: 16 }}>
        <div className={s.card}>
          <div className={s.cardBody}>
            {departments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#8a8886' }}>
                <p>No departments created yet.</p>
                <p style={{ fontSize: 13, marginTop: 8 }}>Create departments to organize your team members.</p>
              </div>
            ) : (
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Department Name</th>
                    <th>Department Owner</th>
                    <th>Members</th>
                    <th>Status</th>
                    <th style={{ width: 100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {departments.map((dept) => (
                    <tr
                      key={dept.id}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: selectedDept?.id === dept.id ? '#f3f2f1' : undefined,
                      }}
                      onClick={() => setSelectedDept(dept)}
                    >
                      <td>
                        <div style={{ fontWeight: 600, color: '#323130' }}>{dept.name}</div>
                        {dept.description && (
                          <div style={{ fontSize: 12, color: '#8a8886', marginTop: 2 }}>{dept.description}</div>
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {orgMember?.user_type === 'owner' || orgMember?.user_type === 'admin' ? (
                          <select
                            className={s.filterSelect}
                            value={dept.manager_id || ''}
                            onChange={(e) => handleUpdateDepartmentOwner(dept.id, e.target.value || null)}
                            style={{ fontSize: '13px', minWidth: 150 }}
                          >
                            <option value="">No owner</option>
                            {orgMembers.map((member) => (
                              <option key={member.user_id} value={member.user_id}>
                                {member.display_name || member.email || member.user_id}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span style={{ fontSize: 13, color: '#605e5c' }}>
                            {(dept as any).manager_name || (dept as any).manager_email || 'No owner'}
                          </span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#605e5c' }}>
                          <Users size={14} />
                          <span>{dept.member_count || 0}</span>
                        </div>
                      </td>
                      <td>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            background: dept.is_active ? '#dff6dd' : '#edebe9',
                            color: dept.is_active ? '#107c10' : '#605e5c',
                          }}
                        >
                          {dept.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDepartment(dept.id);
                          }}
                          className={s.iconBtn}
                          title="Delete department"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {selectedDept && (
          <div className={s.card}>
            <div className={s.cardHeader}>
              <h3 className={s.cardTitle}>{selectedDept.name} Members</h3>
            </div>
            <div className={s.cardBody}>
              {deptMembers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#8a8886' }}>
                  <p>No members in this department yet.</p>
                  <p style={{ fontSize: 13, marginTop: 8 }}>
                    Assign departments when inviting new team members.
                  </p>
                </div>
              ) : (
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Role</th>
                      <th style={{ width: 80 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deptMembers.map((member) => (
                      <tr key={member.id}>
                        <td>{member.display_name}</td>
                        <td>
                          <span style={{ textTransform: 'capitalize' }}>{member.role}</span>
                        </td>
                        <td>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className={s.iconBtn}
                            title="Remove from department"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className={s.modalOverlay}>
          <div className={s.modalContent}>
            <h2 style={{ margin: '0 0 16px', fontSize: 20, color: '#323130' }}>Create Department</h2>

            {saveError && <div className={s.error}>{saveError}</div>}

            <form onSubmit={handleCreateDepartment}>
              <div className={s.formGroup}>
                <label>Department Name *</label>
                <input
                  type="text"
                  value={deptName}
                  onChange={(e) => setDeptName(e.target.value)}
                  placeholder="e.g., Engineering, Sales, Marketing"
                  required
                  autoFocus
                />
              </div>

              <div className={s.formGroup}>
                <label>Description</label>
                <textarea
                  value={deptDescription}
                  onChange={(e) => setDeptDescription(e.target.value)}
                  placeholder="Optional description"
                  rows={3}
                />
              </div>

              <div className={s.formGroup}>
                <label>Department Owner</label>
                <select
                  value={deptManagerId}
                  onChange={(e) => setDeptManagerId(e.target.value)}
                >
                  <option value="">No owner (assign later)</option>
                  {orgMembers.map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.display_name} ({member.user_type})
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 12, color: '#605e5c', marginTop: 4 }}>
                  Department owners can manage their department's data and team members
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className={s.cancelBtn}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className={s.saveBtn} disabled={saving}>
                  {saving ? 'Creating...' : 'Create Department'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
